import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import { User } from "../../../../../api/accessControl/User.ts";
import { BackendApi } from "../../../../../api/api.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import { PrimaryButton } from "../../../../../components/general/PrimaryButton.tsx";
import {
    createTable,
    TableHeaderSpec,
} from "../../../../../components/table/CreateTable.tsx";
import { MoneyLabel } from "../../../../../components/general/MoneyLabel.tsx";
import { toDecimalFromMoney } from "../../../../../lib/Money.ts";
import { useNotifications } from "../../../../../lib/NotificationContext.tsx";
import type { SalesInvoiceDraftRow } from "../../components/salesInvoices/types/salesInvoice.ts";
import { formatDateDDMMYYYY } from "../../components/salesInvoices/utils/salesInvoicesFormat.ts";

function DraftInvoicesTable({
    drafts,
    isLoading,
    onOpen,
    onEdit,
}: {
    drafts: SalesInvoiceDraftRow[];
    isLoading: boolean;
    onOpen: (draft: SalesInvoiceDraftRow) => void;
    onEdit: (draft: SalesInvoiceDraftRow) => void;
}) {
    const tableSpec: TableHeaderSpec<SalesInvoiceDraftRow> = {
        isLoading,
        skeletonRows: 4,
        columns: [
            {
                name: "Factuur",
                field: "invoice_date",
                customRender: (row) => (
                    <div>
                        <div className="font-medium text-gray-900">
                            Concept — nog geen nummer
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                            {formatDateDDMMYYYY(row.invoice_date)}
                        </div>
                    </div>
                ),
                style: { widthMultiplier: 1.5, skeleton: "lead" },
            },
            {
                name: "Klant",
                field: "customer_name",
                customRender: (row) => (
                    <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                            {row.customer_name || "—"}
                        </div>
                        {row.description && (
                            <div className="mt-0.5 text-xs text-gray-500 truncate">
                                {row.description}
                            </div>
                        )}
                    </div>
                ),
                style: { widthMultiplier: 2.5, skeleton: "lead" },
            },
            {
                name: "Bedrag",
                field: "amount",
                customRender: (row) => (
                    <span className="font-medium text-gray-900">
                        <MoneyLabel
                            value={toDecimalFromMoney(row.amount)}
                            showCurrency={true}
                            currency={row.amount.currency}
                        />
                    </span>
                ),
                style: { widthMultiplier: 1 },
            },
            {
                name: "",
                field: "id",
                customRender: (row) => (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(row);
                        }}
                        className="inline-flex items-center gap-1 text-gray-400 hover:text-gray-700 hover:underline text-sm whitespace-nowrap"
                    >
                        <PencilSquareIcon className="h-4 w-4" />
                        Bewerken
                    </button>
                ),
                style: { widthMultiplier: 1 },
            },
        ],
        onEmptyRender: () => (
            <span className="text-gray-500">
                Geen conceptfacturen. Nieuwe facturen die je nog niet verstuurt, verschijnen hier.
            </span>
        ),
        onRowClick: onOpen,
        options: {
            disableTopPaginationWidget: true,
            disableBottomPaginationWidget: false,
            allowFullRowClick: true,
        },
    };
    const TableComponent = createTable<SalesInvoiceDraftRow>(drafts, tableSpec);
    return <>{TableComponent()}</>;
}

export default function DraftInvoicesPage({ currentUser }: { currentUser: User }) {
    const [drafts, setDrafts] = useState<SalesInvoiceDraftRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const api = new BackendApi(currentUser.getToken());
    const { dispatch } = useNotifications();
    const navigate = useNavigate();

    async function onLoad() {
        try {
            const data = await api.Me__business__getSalesInvoiceDrafts({});
            setDrafts(data.drafts);
        } catch (e) {
            console.error(e);
            dispatch({
                title: "Fout bij laden",
                description: "De conceptfacturen konden niet worden geladen. Probeer het later opnieuw.",
                type: "error",
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- async data load, same pattern as the rest of the app
        onLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    return (
        <Shell
            currentUser={currentUser}
            title="Conceptfacturen"
            subtitle="Facturen die nog niet definitief zijn gemaakt — zonder factuurnummer."
            titleActions={
                <PrimaryButton
                    title="Nieuwe factuur"
                    icon={PlusIcon}
                    onClick={() => navigate("/create-invoice")}
                />
            }
        >
            <DraftInvoicesTable
                drafts={drafts}
                isLoading={isLoading}
                onOpen={(draft) => navigate(`/invoices/${draft.id}`)}
                onEdit={(draft) => navigate(`/create-invoice?draft=${draft.id}`)}
            />
        </Shell>
    );
}
